import { Component, Input, OnInit, OnChanges, OnDestroy, AfterViewInit, AfterViewChecked, HostListener, ElementRef, ViewChild, TemplateRef, SimpleChanges, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { NavTab } from './cpq-tabs.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
    selector: 'cpq-tabs',
    templateUrl: './cpq-tabs.component.html',
    styleUrls: ['./cpq-tabs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class CpqTabsComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit, AfterViewChecked {
  @Input('tabs') tabs = Array<NavTab>();
  @Input('selectedTabQueryParamName')selectedTabQueryParamName: string = 'selectedTab';
  @Input('shouldUpdateQueryParam')shouldUpdateQueryParam: boolean = true;

  @Output() activeTabChange = new EventEmitter<number>();

  @ViewChild('dropdownTabComponent') dropdownTabComponent: NavTab;
  @ViewChild('dropdownMenu') dropdownMenu: TemplateRef<any>;
  @ViewChild('dropdownButton') dropdownButton: ElementRef;

  public dropdownTabTemplate: any;
  public navTabs: Array<NavTab> = new Array<NavTab>();
  public dropdownTabs: Array<NavTab> = new Array<NavTab>();
  public activeNavTab: NavTab = new NavTab();
  public activeDropdownTab: NavTab = new NavTab();
  public isDropdownOpen: boolean;
  public activeNavTabIndex = 0;
  private renderedNavTabs: Array<NavTab> = new Array<NavTab>();
  private pendingTabCheck = false;
  private queryParamsSubscription: Subscription;

  private static readonly TAB_GAP = 40;

  constructor(private router: Router, private elRef: ElementRef, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.subscribeToRouteParams();
    this.checkAndMoveTabs();
    if(!this.tabs.some(tab => tab.active)) {
      this.activeTabChange.emit();
     }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tabs && this.tabs) {
      this.verifyTabProperties(this.tabs);
      const tabCountChanged = (this.navTabs.length + this.dropdownTabs.length) !== this.tabs.length;
      if (changes.tabs.firstChange || tabCountChanged) {
        this.navTabs = [...this.tabs];
        this.dropdownTabs = [];
        this.renderedNavTabs = [...this.navTabs];
        if (!changes.tabs.firstChange) {
          this.pendingTabCheck = true;
        }
      } else {
        const refresh = (list: NavTab[]) =>
          list.map(existing => this.tabs.find(t => t.tabIndex === existing.tabIndex) ?? existing);
        this.navTabs = refresh(this.navTabs);
        this.dropdownTabs = refresh(this.dropdownTabs);
        this.renderedNavTabs = [...this.navTabs];
      }
    }
  }
  
  ngAfterViewInit(): void {
    this.checkAndMoveTabs();
  }

  // CheckAndMoveTabs() is only executed
  // when pendingTabCheck is true — set in ngOnChanges when the tabs input changes (e.g. after save).
  // This ensures tab widths can be measured correctly for overflow/dropdown calculation
  ngAfterViewChecked(): void {
    if (this.pendingTabCheck) {
      this.pendingTabCheck = false;
      this.checkAndMoveTabs();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.checkAndMoveTabs();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.dropdownButton && !this.dropdownButton.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  public toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  public onSelectedIndexChange(index: number): void {
    const tab = this.renderedNavTabs[index];
    if (tab && !(tab.disabled && tab.disabled())) {
      this.clearActiveDropdownTab();
      this.changeNavTab(tab.tabIndex);
    }
  }

  public onNavTabClick(navTab: NavTab): void {
    if (this.activeDropdownTab?.active && !(navTab.disabled && navTab.disabled())) {
      this.clearActiveDropdownTab();
      this.changeNavTab(navTab.tabIndex);
    }
  }

  private clearActiveDropdownTab(): void {
    if (this.activeDropdownTab?.active) {
      this.activeDropdownTab.active = false;
      this.activeDropdownTab = new NavTab();
      this.dropdownTabTemplate = null;
    }
  }

  public changeNavTab(tabIndex: number): void {
    this.navTabs.forEach((tab, index) => {
      if (tab.tabIndex === tabIndex) {
        tab.active = true;
        this.activeNavTab = tab;
        this.activeNavTabIndex = index;
      } else {
        tab.active = false;
      }
    });

    this.dropdownTabs.forEach(dropdownTab => dropdownTab.active = false);
    this.activeTabChange.emit(tabIndex);

    if (this.shouldUpdateQueryParam && this.navTabs.find(tab => tab.active === true).tabIndex === tabIndex) {
      const queryParams = this.createQueryParamsObject(tabIndex);
      this.router.navigate([], { queryParams: queryParams });
    }
  }

  public changeDropdownTab(tabIndex: number): void {
    this.dropdownTabs.forEach(tab => {
      if (tab.tabIndex === tabIndex) {
        tab.active = true;
        this.activeDropdownTab = tab;
      } else {
        tab.active = false;
      }
    });
    this.activeTabChange.emit(tabIndex);

    if (this.shouldUpdateQueryParam && this.dropdownTabs.find(tab => tab.active === true).tabIndex === tabIndex) {
      const queryParams = this.createQueryParamsObject(tabIndex);
      this.router.navigate([], { queryParams: queryParams });
    }
  }

  public activateDropdownTab(dropdownTab: NavTab) {
    if (dropdownTab.disabled && dropdownTab.disabled()) {
      return;
    }
    this.dropdownTabTemplate = dropdownTab.tabTemplate;
    this.changeDropdownTab(dropdownTab.tabIndex);
    this.navTabs.forEach((tab: NavTab) => tab.active = false);
  }

  private updateTabsComponent(tabId: number): void {
    const isTabInNavbar = this.navTabs.some(tab => tab.tabIndex === tabId);
    const isTabInDropdown = this.dropdownTabs.some(tab => tab.tabIndex === tabId);

    if (isTabInNavbar) {
      this.changeNavTab(tabId);
    } else if (isTabInDropdown) {
      const dropdownTab = this.dropdownTabs.find(tab => tab.tabIndex === tabId);
      if (dropdownTab) {
        this.activateDropdownTab(dropdownTab);
      }
    }
  }

  private subscribeToRouteParams(): void {
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      const selectedTab = +params[this.selectedTabQueryParamName];
      this.updateTabsComponent(selectedTab);
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
  }

  private checkAndMoveTabs(): void {
    this.moveNavTabs();
    this.moveDropdownTabs();
    this.renderedNavTabs = [...this.navTabs];
  }
  
  private moveNavTabs(): void {
    let navTabsWidth = this.getNavTabsWidth(this.navTabs);
    let firstDropdownTabWidth = this.dropdownTabs[0]?.tabWidth || 0;
    const tabContainer = this.elRef.nativeElement.querySelector('#tabContainer');

    while (navTabsWidth + firstDropdownTabWidth < tabContainer.offsetWidth && this.dropdownTabs.length > 0) {
      const firstTabInDropdown = this.dropdownTabs.shift();
      if (!this.dropdownTabs.includes(firstTabInDropdown)){
        this.navTabs.push(firstTabInDropdown);
      }

      if (this.activeDropdownTab.tabIndex === firstTabInDropdown.tabIndex && this.activeDropdownTab.active) {
        this.activeNavTab = firstTabInDropdown;
        this.activeDropdownTab = new NavTab();
        this.activeNavTabIndex = this.navTabs.findIndex(t => t.tabIndex === firstTabInDropdown.tabIndex);
      }

      navTabsWidth = this.getNavTabsWidth(this.navTabs);
      firstDropdownTabWidth = this.dropdownTabs[0]?.tabWidth || 0;
    }
  }
  
  private moveDropdownTabs(): void {
    let navTabsWidth = this.getNavTabsWidth(this.navTabs);
    const tabContainer = this.elRef.nativeElement.querySelector('#tabContainer');

    while (navTabsWidth > tabContainer.offsetWidth && this.navTabs.length > 0) {
      const lastNavTab = this.navTabs.pop();
      if (lastNavTab && !this.dropdownTabs.includes(lastNavTab)) {
        this.dropdownTabs.unshift(lastNavTab);
  
        if (this.activeNavTab && this.activeNavTab.tabIndex === lastNavTab.tabIndex) {
          this.dropdownTabTemplate = lastNavTab.tabTemplate;
          this.activeDropdownTab = lastNavTab;
          this.activeNavTabIndex = 0;
          this.navTabs.forEach(t => t.active = false);
        }
      }
  
      navTabsWidth = this.getNavTabsWidth(this.navTabs);
    }
  }
 
  private getNavTabsWidth(navTabs: Array<NavTab>): number {
    let width = 0;
    const tabItems = this.elRef.nativeElement.querySelectorAll('#tabContainer .mat-mdc-tab');
  
    tabItems.forEach((tabItem, index) => {
      const tabName = tabItem.innerText;
      const tab = navTabs.find(tab => tab.name === tabName);
  
      if (tab) {
        if (index === 0) {
          width += tab.tabWidth = tabItem.offsetWidth;
        } else {
          width += tab.tabWidth = tabItem.offsetWidth + CpqTabsComponent.TAB_GAP;
        }
      }
    });
  
    return width;
  }

  private createQueryParamsObject(tabIndex: number): any {
    const queryParams = {};
    queryParams[this.selectedTabQueryParamName] = tabIndex;
    return queryParams;
  }

  private verifyTabProperties(tabs: Array<NavTab>): void {
    const tabIndexSet = new Set();
    tabs.forEach(tab => {
      if (tabIndexSet.has(tab.tabIndex)) {
        throw new Error(`Duplicate tabIndex detected for tab: ${tab.name}, duplicated tabIndex: ${tab.tabIndex}`);
      }
      tabIndexSet.add(tab.tabIndex);

      if (!tab.tabTemplate) {
        throw new Error(`tabTemplate is empty for tab: ${tab.name}`);
      }
    });
  }

  public areTabsValid(tabs: Array<NavTab>): boolean {
    return tabs.some(tab => tab.valid && !tab.valid());
  }
}
